// pages/WebsiteLinks/EditSummary.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Button, Modal, Input, Space, Select, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { marked } from 'marked';

const { Option } = Select;

// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

export default function EditSummary({ document, onClose, onUpdate, chatbotId, permission   }) {
  const [summaryContent, setSummaryContent] = useState("<p></p>");
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState('save');

  const summaryEditor = useEditor({
    extensions: [
        StarterKit.configure({}),
        Underline,
        Table.configure({ resizable: true }),
        TableCell, TableHeader, TableRow,
        Link.configure({
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
            HTMLAttributes: {
                target: '_blank',
                rel: 'noopener noreferrer',
            },
        }),
        Image.configure({
            inline: true,
            allowBase64: true,
            HTMLAttributes: {
                class: 'tiptap-image',
            },
        }),
    ],
    content: summaryContent,
    onUpdate: ({ editor }) => setSummaryContent(editor.getHTML()),
    editorProps: { attributes: { class: 'tiptap-editor-area' } },
  });

  useEffect(() => {
    if (document) {
      const summaryAsHtml = document.summary ? marked.parse(document.summary, { gfm: true }) : "<p></p>";
      if (summaryEditor && summaryEditor.getHTML() !== summaryAsHtml) {
          summaryEditor.commands.setContent(summaryAsHtml, false);
      }
    }
  }, [document, summaryEditor]);

  // <<< START: NEW CODE (Requirement 1) >>>
  // Handle Ctrl/Cmd + click on links to open in a new tab
  useEffect(() => {
    if (!summaryEditor || !summaryEditor.view.dom) return;
    
    const editorElement = summaryEditor.view.dom;
    const handleLinkClick = (event) => {
        const link = event.target.closest('a');
        if (link && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            window.open(link.href, '_blank', 'noopener, noreferrer');
        }
    };

    editorElement.addEventListener('click', handleLinkClick);

    return () => {
        editorElement.removeEventListener('click', handleLinkClick);
    };
  }, [summaryEditor]);
  // <<< END: NEW CODE >>>


  const setLink = useCallback(() => {
    if (!summaryEditor) return;
    const previousUrl = summaryEditor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      summaryEditor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const validatedUrl = ensureUrlProtocol(url);
    summaryEditor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [summaryEditor]);

  // <<< START: MODIFIED CODE (Requirement 2) >>>
  // Updated to show existing image URL in the prompt for editing
  const addImage = useCallback(() => {
    if (!summaryEditor) return;
    const existingUrl = summaryEditor.getAttributes('image').src;
    const url = window.prompt('Enter image URL:', existingUrl || '');
    if (url) {
      summaryEditor.chain().focus().setImage({ src: url }).run();
    }
  }, [summaryEditor]);
  // <<< END: MODIFIED CODE >>>

  const handleSave = async () => {
    if (!summaryEditor) {
        setError("Editor is not ready.");
        return;
    }
    setIsSaving(true);
    setToolbarSaveStatus('saving');
    setError(null);

    try {
      
      const summaryToSave = summaryEditor.getHTML();

      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      // IMPORTANT: We send the original page_content along with the new summary
      const payload = { 
        id: document.id, 
        page_content: document.page_content, // Keep original page content
        summary: summaryToSave, // Send new summary
        all_data: document // Send original document as base
      };

      const response = await axiosInstance.post(
        `/update-trained-data`,
          payload, {
            params
          }
      );

      if (response.status === 200) {
        onUpdate();
        setToolbarSaveStatus('saved');
        setTimeout(() => setToolbarSaveStatus('save'), 1500);
      } else {
         setError(`Failed to update data (Status: ${response.status})`);
         setToolbarSaveStatus('save');
      }
    } catch (err) {
      const message = err.response?.data?.message || "Failed to update summary.";
      setError(message);
      setToolbarSaveStatus('save');
      console.error("Error updating summary:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const handleInsertTable = useCallback(() => {
    if (!summaryEditor) return;
    const rows = parseInt(tableRows, 10);
    const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) {
        summaryEditor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }
    setIsTableDialogOpen(false);
  }, [summaryEditor, tableRows, tableColumns]);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);

  const handleHeadingChange = useCallback((value) => {
    if (!summaryEditor) return;
    if (value === 'paragraph') {
        summaryEditor.chain().focus().setParagraph().run();
    } else {
        const level = parseInt(value.slice(1), 10);
        if (level >= 1 && level <= 6) {
           summaryEditor.chain().focus().toggleHeading({ level }).run();
        }
    }
  }, [summaryEditor]);
  
  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={() => summaryEditor?.chain().focus().addColumnAfter().run()} disabled={!summaryEditor?.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={() => summaryEditor?.chain().focus().addRowAfter().run()} disabled={!summaryEditor?.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={() => summaryEditor?.chain().focus().deleteColumn().run()} disabled={!summaryEditor?.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={() => summaryEditor?.chain().focus().deleteRow().run()} disabled={!summaryEditor?.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={() => summaryEditor?.chain().focus().deleteTable().run()} disabled={!summaryEditor?.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );

  const currentHeadingLevel = () => {
    if (!summaryEditor) return 'paragraph';
    for (let i = 1; i <= 6; i++) {
      if (summaryEditor.isActive('heading', { level: i })) return `h${i}`;
    }
    return 'paragraph';
  };

  if (!summaryEditor) {
    return <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-2x"></i> Loading Editor...</div>;
  }

  return (
    <div className="edit-summary-data" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ paddingBottom: '10px', borderBottom: '1px solid #eee', marginBottom: '10px', flexShrink: 0 }}>
        <h5 style={{ margin: 0, fontSize: '0.95rem', wordBreak: 'break-word' }}>Edit Summary for: {document?.website_link}</h5>
      </div>

      <div className="sticky-toolbar" style={{ flexShrink: 0, marginBottom: '10px', border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: '#f8f9fa' }}>
        <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
          <button onClick={() => summaryEditor?.chain().focus().toggleBold().run()} className={summaryEditor?.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fa fa-bold"></i></button>
          <button onClick={() => summaryEditor?.chain().focus().toggleItalic().run()} className={summaryEditor?.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fa fa-italic"></i></button>
          <button onClick={() => summaryEditor?.chain().focus().toggleUnderline().run()} className={summaryEditor?.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fa fa-underline"></i></button>
          <button onClick={() => summaryEditor?.chain().focus().toggleStrike().run()} className={summaryEditor?.isActive('strike') ? 'is-active' : ''} title="Strike"><i className="fa fa-strikethrough"></i></button>
          <button onClick={setLink} className={summaryEditor?.isActive('link') ? 'is-active' : ''} title="Link"><i className="fa fa-link"></i></button>
          <button onClick={addImage} title="Add Image"><i className="fa fa-image"></i></button>
          <Select value={currentHeadingLevel()} style={{ width: 120 }} onChange={handleHeadingChange}>
            <Option value="paragraph">Paragraph</Option>
            {[1, 2, 3, 4, 5, 6].map(level => <Option key={level} value={`h${level}`}>Heading {level}</Option>)}
          </Select>
          <button onClick={() => summaryEditor?.chain().focus().toggleBulletList().run()} className={summaryEditor?.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fa fa-list-ul"></i></button>
          <button onClick={() => summaryEditor?.chain().focus().toggleOrderedList().run()} className={summaryEditor?.isActive('orderedList') ? 'is-active' : ''} title="Ordered List"><i className="fa fa-list-ol"></i></button>
          <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1500 }}>
            <Button style={{ marginLeft: '8px', marginRight: '8px' }}><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></Button>
          </Dropdown>
          <button onClick={() => summaryEditor?.chain().focus().undo().run()} disabled={!summaryEditor?.can().undo()} title="Undo"><i className="fa fa-undo"></i></button>
          <button onClick={() => summaryEditor?.chain().focus().redo().run()} disabled={!summaryEditor?.can().redo()} title="Redo"><i className="fa fa-repeat"></i></button>
          {permission.canModify && (
            <button onClick={handleSave} title="Save Changes">
              {toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}
            </button>
          )}
        </div>
      </div>
      
      <div className="editor-container" style={{ flexGrow: 1, overflow: 'hidden', marginBottom: '15px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
            <label htmlFor="summary-editor" style={{ fontWeight: 'bold', margin: '5px 0 5px 10px', display: 'block', flexShrink: 0 }}>Summary</label>
            <div style={{ flexGrow: 1, overflowY: 'auto' }}>
                <EditorContent editor={summaryEditor} />
            </div>
        </div>
      </div>

      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #eee', flexShrink: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: '1 1 100%', minWidth: 0 }}>
          {error && <div className="alert alert-danger" style={{ margin: 0, padding: '0.5rem 1rem', fontSize: '0.8rem', wordBreak: 'break-word' }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {permission.canModify && (
            <Button type="primary" onClick={handleSave} loading={isSaving} icon={<i className="fa fa-save" style={{ marginRight: '4px' }}></i>} style={{ marginRight: '0' }}>
              {isSaving ? 'Saving...' : 'Save Summary'}
            </Button>
          )}
          <Button onClick={onClose} disabled={isSaving} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </Button>
        </div>
      </div>

      <Modal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1500}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
          <Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
        </Space>
      </Modal>
      
      <style jsx global>{`
            .tiptap-editor-area {
                min-height: 200px;
                max-height: calc(85vh - 350px);
                overflow-y: auto;
                padding: 0.5rem;
            }
            @media (min-width: 600px) { .tiptap-editor-area { min-height: 400px; } }
            .ProseMirror { outline: none; }
            .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
            .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
            .ProseMirror li { margin-bottom: 0.1rem; }
            .ProseMirror li p { margin-bottom: 0.1rem; }
            .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
            .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 { margin-top: 0.75rem; margin-bottom: 0.3rem; line-height: 1.3; font-weight: 500; }
            .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
            .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
            .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
            .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; }
            .ProseMirror img.ProseMirror-selectednode { outline: 3px solid #68CEF8; }
        `}</style>
    </div>
  );
}